import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { signOut } from "aws-amplify/auth";
import { toast } from "react-toastify";

import { isTransientAuthSession } from "../utils/transient-auth-session";

/**
 * Listens for auth error events dispatched by api.client and reacts globally:
 *
 *   kaagazy:device-verification-required  →  redirect to /login (login page
 *     shows OTP screen because checkAlreadySignedIn will get the 403 again)
 *
 *   kaagazy:session-revoked  →  clear Cognito session + redirect to /login
 *     with a "you've been signed out" toast
 *
 *   kaagazy:account-disabled  →  same as session-revoked but different message
 *
 * Must be mounted inside <BrowserRouter> so useNavigate is available.
 * Does nothing when already on /login to avoid re-triggering loops.
 */
export function AuthInterceptor() {
  const navigate = useNavigate();
  const location = useLocation();
  // Prevent duplicate handling if several in-flight requests all fail at once.
  const handlingRef = useRef(false);

  useEffect(() => {
    const onLoginPage = () => location.pathname === "/login";

    const forceReLogin = async (message: string) => {
      if (onLoginPage() || handlingRef.current) return;
      handlingRef.current = true;
      try {
        await signOut();
      } catch {
        // best-effort — clear whatever Amplify has locally
      }
      toast.error(message, { toastId: "auth-force-relogin" });
      navigate("/login", { replace: true });
      // Reset after navigation so a fresh login can trigger again if needed.
      setTimeout(() => { handlingRef.current = false; }, 2000);
    };

    const onDeviceChallenge = () => {
      // A password-setup flow owns its session and signs out of it itself. The
      // device-trust 403s its short-lived session collects are not a device
      // challenge for anyone: acting on them lands the user on the OTP screen
      // whose first call then goes out with no token at all.
      if (isTransientAuthSession()) return;
      if (onLoginPage() || handlingRef.current) return;
      handlingRef.current = true;
      // Don't sign out — Cognito session is still valid.
      // Redirecting to /login causes checkAlreadySignedIn → redirectAfterLogin
      // → 403 device_verification_required → OTP screen.
      navigate("/login", { replace: true });
      setTimeout(() => { handlingRef.current = false; }, 2000);
    };

    const onSessionRevoked = () =>
      forceReLogin("Your session was signed out from another device. Please sign in again.");

    const onAccountDisabled = () =>
      forceReLogin("Your account has been disabled. Please contact your clinic admin.");

    window.addEventListener("kaagazy:device-verification-required", onDeviceChallenge);
    window.addEventListener("kaagazy:session-revoked", onSessionRevoked);
    window.addEventListener("kaagazy:account-disabled", onAccountDisabled);

    return () => {
      window.removeEventListener("kaagazy:device-verification-required", onDeviceChallenge);
      window.removeEventListener("kaagazy:session-revoked", onSessionRevoked);
      window.removeEventListener("kaagazy:account-disabled", onAccountDisabled);
    };
  }, [navigate, location.pathname]);

  return null;
}
